import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, Search } from "lucide-react"
import type * as React from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { cn } from "@/lib/utils"
import { Input } from "./input"

// Shared trigger styles for consistency with regular Select
const SELECT_TRIGGER_STYLES =
  "border-input data-placeholder:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

const SELECT_ITEM_STYLES =
  "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2"

interface FilterableSelectProps extends React.ComponentProps<typeof SelectPrimitive.Root> {
  options: Array<{ id: string; label: string }>
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  value?: string
  onValueChange?: (value: string) => void
  className?: string
  size?: "sm" | "default"
}

function FilterableSelect({
  options,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  noResultsText = "No results found",
  value,
  onValueChange,
  className,
  size = "default",
  ...props
}: FilterableSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const itemsContainerRef = useRef<HTMLDivElement>(null)

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options
    }
    const query = searchQuery.toLowerCase()
    return options.filter((option) => option.label.toLowerCase().includes(query))
  }, [options, searchQuery])

  // Get the label for the selected value
  const selectedLabel = useMemo(() => {
    const selected = options.find((opt) => opt.id === value)
    return selected?.label || placeholder
  }, [options, value, placeholder])

  // Reset search when dropdown closes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSearchQuery("")
      setHighlightedIndex(-1)
    } else {
      // Focus the input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        if (highlightedIndex === -1) {
          // Move from input to first item
          setHighlightedIndex(0)
        } else if (highlightedIndex < filteredOptions.length - 1) {
          setHighlightedIndex(highlightedIndex + 1)
        }
        break
      case "ArrowUp":
        e.preventDefault()
        if (highlightedIndex > 0) {
          setHighlightedIndex(highlightedIndex - 1)
        } else if (highlightedIndex === 0) {
          // Move back to input
          setHighlightedIndex(-1)
          searchInputRef.current?.focus()
        }
        break
      case "Enter":
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          onValueChange?.(filteredOptions[highlightedIndex].id)
          setIsOpen(false)
        }
        break
      case "Escape":
        e.preventDefault()
        setIsOpen(false)
        break
    }
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && itemsContainerRef.current) {
      const items = itemsContainerRef.current.querySelectorAll("[data-filterable-item]")
      const highlightedItem = items[highlightedIndex] as HTMLElement
      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: "nearest" })
      }
    }
  }, [highlightedIndex])

  // Reset highlighted index when search changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only searchQuery should trigger reset, not setHighlightedIndex
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchQuery])

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      onOpenChange={handleOpenChange}
      open={isOpen}
      {...props}
    >
      <SelectPrimitive.Trigger
        data-slot="select-trigger"
        data-size={size}
        className={cn(SELECT_TRIGGER_STYLES, className)}
      >
        <SelectPrimitive.Value data-slot="select-value" placeholder={placeholder}>
          {selectedLabel}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          data-slot="select-content"
          className={cn(
            "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-32 origin-(--radix-select-content-transform-origin) overflow-hidden rounded-md border shadow-md",
          )}
          position="popper"
          align="center"
          onKeyDown={handleKeyDown}
        >
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Prevent Radix Select from handling keyboard events on the input
                  if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) {
                    e.stopPropagation()
                  }
                  handleKeyDown(e)
                }}
                className="pl-8 h-8"
                autoFocus
              />
            </div>
          </div>

          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            ref={itemsContainerRef}
            className={cn(
              "p-1 h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1",
            )}
          >
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">{noResultsText}</div>
            ) : (
              filteredOptions.map((option, index) => (
                <SelectPrimitive.Item
                  key={option.id}
                  value={option.id}
                  data-slot="select-item"
                  data-filterable-item
                  data-highlighted={index === highlightedIndex}
                  className={cn(
                    SELECT_ITEM_STYLES,
                    index === highlightedIndex && "bg-accent text-accent-foreground",
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => {
                    if (highlightedIndex === index) {
                      setHighlightedIndex(-1)
                    }
                  }}
                >
                  <span className="absolute right-2 flex size-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <CheckIcon className="size-4" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))
            )}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export { FilterableSelect }
